import { NOTIFICATIONS_ROOT } from '@/data/entities/notifications';
import { snackbarColor } from '@/enums';
import { useSnackbar } from '@/hooks';
import type { INotification, ISocketNotification } from '@/types';
import { useSyncedChannel } from './useSyncedChannel';

/**
 * Subscribes to real-time notification updates for the current user via Pusher.
 * Call alongside useNotificationsQuery() to get live updates.
 *
 * Uses useSyncedChannel to buffer WebSocket events during in-flight mutations,
 * preventing race conditions between optimistic updates and server echoes.
 */
export function useNotificationWebSocket(userId: number | null, token: string) {
    const showSnackbar = useSnackbar();

    useSyncedChannel<INotification[], ISocketNotification>({
        queryKey: NOTIFICATIONS_ROOT,
        channel: `App.Models.User.${userId}`,
        token,
        enabled: !!userId,
        skipOwn: false,
        events: {
            notification: {
                update: (old, n) => {
                    if (!old) return old;
                    return [
                        {
                            id: n.id,
                            type: n.type,
                            notifiable_id: n.notifiable_id,
                            created_at: n.created_at,
                            updated_at: n.updated_at,
                            data: {
                                project_id: n.project_id,
                                action_url: n.comment?.action_url,
                                message: n.message,
                                comment: n.comment,
                                commentable: n.commentable,
                                thread: n.thread,
                            },
                            read_at: null,
                        },
                        ...old,
                    ];
                },
            },
        },
        onEvent: (n: ISocketNotification) => {
            showSnackbar({
                title: n.message,
                message: n.comment?.text,
                vertical: 'top',
                horizontal: 'right',
                open: true,
                severity: snackbarColor.info,
            });
        },
    });
}
